import { useEffect } from 'react'
import classNames from 'classnames'
import isNil from 'lodash-es/isNil'

import { INPUT_AUTOCOMPLETE_VALUE } from '../../../constants/common'
import { useContentOverflow } from '../../../hooks'
import { FloatingTooltip } from '../../Floating'

import * as styles from './Input.module.css'

/**
 * @property {boolean} [blocked]
 * blocked: behaves like disabled (no clicks, no value changes),
 * but keeps the normal visual style (no opacity change) and
 * shows a "not-allowed" cursor. Unlike "readonly", it should not
 * handle clicks or focus changes at all.
 *
 * @property {boolean} [disabled]
 * disabled: the control is visually and functionally disabled
 *
 * @property {boolean} [readonly]
 * readonly: value cannot be changed but the control can still receive focus
 */
function Input({
  Component = 'input',
  autoComplete = INPUT_AUTOCOMPLETE_VALUE.off,
  blocked,
  className = styles.Input,
  disabled,
  focused,
  forceBlur,
  forceFocus,
  readonly,
  ref,
  value,
  ...restProps
}) {
  const {
    overflow: inputValueOverflow,
    ref: inputRef,
    setContentOverflowRef,
  } = useContentOverflow({
    dep: [value],
    externalRef: ref,
  })

  useEffect(() => {
    if (forceBlur && focused) {
      inputRef?.current?.blur()
    }
  }, [focused, forceBlur])

  useEffect(() => {
    if (forceFocus && !focused) {
      inputRef?.current?.focus()
    }
  }, [focused, forceFocus])

  const mods = {
    [styles.blocked]: blocked,
    [styles.disabled]: disabled,
    [styles.readonly]: readonly,
  }

  const inputClassName = classNames(className, mods)

  // React 19 warns when a controlled input receives `value` without `onChange`.
  // Display-only inputs (e.g. non-searchable selects) are read-only by nature.
  const isReadOnly =
    restProps.readOnly ?? (!isNil(value) && isNil(restProps.onChange))

  return (
    <FloatingTooltip content={value} disabled={!inputValueOverflow}>
      <Component
        {...restProps}
        autoComplete={autoComplete}
        className={inputClassName}
        disabled={disabled || blocked}
        readOnly={isReadOnly}
        ref={setContentOverflowRef}
        value={value}
      />
    </FloatingTooltip>
  )
}

export default Input
